"use client";

import { type ModuleContact } from "@lib/types";
import Image from "next/image";

import { useSettings } from "@/components/providers/SiteDataProvider";
import { ContactInfo } from "@/components/shared/ContactInfo";
import { CtaFlexible } from "@/components/shared/cta";
import CustomPortableText from "@/components/shared/portable-text";
import { ResponsiveImage } from "@/components/shared/ResponsiveImage";
import { SocialLinks } from "@/components/shared/SocialLinks";
import { cn } from "@/lib/ui/cn";

import { Container } from "../ui/container";
import { iconImage } from "../ui/icons";
import { Separator } from "../ui/separator";

type ContactModuleProps = {
  module: ModuleContact;
};

export function ContactModule({ module }: ContactModuleProps) {
  const settings = useSettings();

  // Use shared module if enabled, otherwise use inline module data
  const effectiveModule =
    module.useSharedModule && settings?.sharedContactModule ? settings.sharedContactModule : module;
  return (
    <Container
      as="section"
      className={cn(
        "bg-gradient-to-b from-[#000003] via-[#13233D] to-[#000212]",
        "pb-8 md:pb-12 lg:pb-20",
        "flex flex-col items-center gap-2.5",
      )}
    >
      {/* Header Section - Full Width on Desktop */}
      <div
        className={cn(
          "w-full",
          "px-3 pt-10 pb-8",
          "md:px-6 md:pt-16 md:pb-12",
          "lg:px-8 lg:pt-28 lg:pb-20",
          "flex flex-col items-start justify-end gap-4 md:gap-6",
        )}
      >
        {effectiveModule.title && (
          <h2
            className={cn(
              "w-full",
              "max-w-96 md:max-w-[640px] lg:max-w-full",
              "text-3xl leading-10 md:text-5xl md:leading-[57px] lg:text-6xl lg:leading-[72px]",
              "font-light tracking-wide text-white",
            )}
          >
            {effectiveModule.title}
          </h2>
        )}
        {effectiveModule.body && (
          <div
            className={cn(
              "w-full",
              "max-w-96 md:max-w-[640px] lg:max-w-full",
              "text-lg leading-5 md:text-xl md:leading-7 lg:text-2xl lg:leading-9",
              "font-normal text-white",
            )}
          >
            <CustomPortableText value={effectiveModule.body} />
          </div>
        )}
      </div>

      {/* Main Content */}
      <div
        className={cn(
          "w-full",
          "max-w-[765px] px-3",
          "md:max-w-[1024px] md:px-6",
          "lg:max-w-[1400px] lg:px-8",
          "flex flex-col items-start gap-4 md:gap-6",
        )}
      >
        {/* Mobile: Stacked Layout - Cards First, Then Contact Info */}
        <div className="flex w-full flex-col gap-4 md:hidden">
          {/* Contact Info Section */}
          <div className="flex w-full flex-col gap-8">
            {/* Info Section and Contact Info */}
            <div className="flex flex-col gap-4">
              {effectiveModule.infoSection && (
                <div className="border-indigo-lighter flex flex-col gap-2 border-b py-4">
                  <div className="text-white">
                    <CustomPortableText value={effectiveModule.infoSection} />
                  </div>
                </div>
              )}
              <span className="text-lg leading-6 font-medium text-white">Company Location</span>
              <Separator className="from-indigo-lighter/50 to-indigo-dark/50 bg-gradient-to-b" />
              <ContactInfo />
            </div>

            {/* Social Links */}
            <div className="gap-gap-element-md flex flex-col">
              <span className="text-lg leading-6 font-medium text-white">Follow Us</span>
              <Separator className="from-indigo-lighter/50 to-indigo-dark/50 bg-gradient-to-b" />
              <SocialLinks />
            </div>
          </div>
          {/* Cards */}
          <div className="flex flex-col gap-4">
            {effectiveModule.cards?.map((card) => (
              <ContactCard key={card._key} card={card} />
            ))}
          </div>
        </div>

        {/* Tablet: 2x2 Grid Layout - Only visible between md and lg breakpoints */}
        <div className="hidden md:grid md:grid-cols-2 md:gap-4 lg:hidden">
          <div className="flex flex-col gap-6">
            {effectiveModule.infoSection && (
              <div className="border-indigo-lighter flex flex-col gap-2 border-b py-6">
                <div className="text-white">
                  <CustomPortableText value={effectiveModule.infoSection} />
                </div>
              </div>
            )}
            <div className="typography-text-large-medium leading-7 text-white">
              Company Location
            </div>
            <Separator className="from-indigo-lighter/50 to-indigo-dark/50 bg-gradient-to-b" />
            <ContactInfo />
          </div>
          {/* Bottom Row: Contact Info */}
          <div className="gap-gap-element-lg flex flex-col">
            <span className="typography-text-large-medium leading-7 text-white">Follow Us</span>
            <Separator className="from-indigo-lighter/50 to-indigo-dark/50 bg-gradient-to-b" />
            <SocialLinks />
          </div>
          {/* Top Row: Cards */}
          {effectiveModule.cards?.map((card) => (
            <ContactCard key={card._key} card={card} />
          ))}
        </div>

        {/* Desktop: 3-Column Equal Layout - Left: Info, Right: 2 Cards */}
        <div className="hidden lg:grid lg:h-[536px] lg:w-full lg:grid-cols-3 lg:gap-6">
          {/* Left Column: Info Section, Social Links, Contact Info */}
          <div className="flex w-full flex-col items-start gap-6">
            <div className="gap-gap-element-md flex w-full flex-col">
              {effectiveModule.infoSection && (
                <div className="border-indigo-lighter flex flex-col gap-2 border-b py-6">
                  <div className="text-white">
                    <CustomPortableText value={effectiveModule.infoSection} />
                  </div>
                </div>
              )}
              <span className="typography-text-large-medium leading-7 text-white">
                Company Location
              </span>
              <ContactInfo />
            </div>
            <Separator className="from-indigo-lighter/50 to-indigo-dark/50 bg-gradient-to-b" />
            <div className="gap-gap-element-md flex flex-col">
              <span className="typography-text-large-medium leading-7 text-white">Follow Us</span>
              <SocialLinks />
            </div>
          </div>

          {/* Right Columns: 2 Cards (equal width) */}
          {effectiveModule.cards?.map((card) => (
            <ContactCard key={card._key} card={card} />
          ))}
        </div>
      </div>
    </Container>
  );
}

function ContactCard({ card }: { card: NonNullable<ModuleContact["cards"]>[number] }) {
  const icon = card.iconName ? iconImage(`Deafult${card.iconName}`) : null;

  return (
    <div
      className={cn(
        "rounded-xl",
        "flex flex-col",
        "relative",
        "p-3 md:p-6 lg:p-8",
        "bg-blend-soft-light backdrop-blur-md",
        "outline-indigo-lighter outline-[3px] outline-offset-[-3px]",
        "bg-[linear-gradient(180deg,rgba(189,212,252,0.20)_0%,rgba(178,178,179,0.20)_100%)]",
        // Card dimensions
        "h-[228px] w-full max-w-[393px]",
        "md:h-[297px] md:w-full",
        "lg:h-[536px] lg:w-full",
      )}
    >
      {/* Icon - Top Right (Desktop) or Top Left (Tablet/Mobile) */}
      <div
        className={cn(
          "flex items-start",
          "lg:absolute lg:top-0 lg:right-0 lg:pt-8 lg:pr-8 lg:pb-0 lg:pl-0",
          "md:justify-start",
          "justify-start",
          "shrink-0",
          "mb-2 md:mb-0",
        )}
      >
        {card.icon ? (
          <ResponsiveImage
            src={card.icon}
            width={160}
            height={160}
            className={cn("object-cover opacity-80", "h-16 w-16 md:h-24 md:w-24 lg:h-40 lg:w-40")}
            imageOptions={{ desktopWidth: 160, tabletWidth: 96, mobileWidth: 64 }}
            useNextImage
          />
        ) : icon ? (
          <Image
            src={icon}
            alt="Icon"
            width={160}
            height={160}
            className={cn("object-cover opacity-80", "h-16 w-16 md:h-24 md:w-24 lg:h-40 lg:w-40")}
          />
        ) : null}
      </div>

      {/* Content - Bottom Section - Aligned across cards */}
      <div className="mt-auto flex min-h-0 flex-col gap-2 self-stretch">
        {/* Text Content - Consistent spacing for alignment */}
        <div className="text-indigo-lighter flex flex-col gap-2">
          {card.title && (
            <h5
              className={cn(
                "text-lg leading-6 md:text-2xl md:leading-7 lg:text-3xl lg:leading-10",
                "text-indigo-lighter font-medium",
              )}
            >
              {card.title}
            </h5>
          )}
          {card.description && (
            <div
              className={cn(
                "text-base leading-6 md:text-lg md:leading-7 lg:text-xl lg:leading-8",
                "text-indigo-lighter font-normal",
              )}
            >
              {card.description}
            </div>
          )}
        </div>

        {/* CTA - Consistent spacing for alignment - Prevent full width */}
        {card.cta && (
          <div className="w-fit">
            <CtaFlexible cta={card.cta as Parameters<typeof CtaFlexible>[0]["cta"]} />
          </div>
        )}
      </div>
    </div>
  );
}
